<template lang="html">
  <div class="chat-wrapper">
    <div>
      <div class="chat-header">
        <div class="left">
          <span>{{ userData.usr_name }}</span>
          <span class="email-dim">{{ userData.usr_email }}</span>
        </div>
        <div class="right">
          <span @click="$emit('closeChat')" class="icon-wrap">
            <Icon icon="mdi:chevron-left" class="icon" />
            {{ $t("main.goBack") }}
          </span>
        </div>
      </div>
      <div class="message-wrap" ref="chatWrap" @scroll="chatScrolled">
        <div class="new-message-overlay">
          <div
            class="new-message-toast"
            v-if="showNewMsgToast"
            @click="scrollToBottom"
          >
            {{ $t("profile.messages.new-message") }}
            <span>
              <Icon icon="mdi:chevron-down" class="icon" />
            </span>
          </div>
        </div>
        <div class="message" v-for="message in messages" :key="message.msg_id">
          <div class="message-header">
            <div class="image-wrap">
              <img :src="getImage(message.sender)" :alt="message.usr_name" />
            </div>
            <span>{{ message.usr_name }}</span>
            <span class="you-symbol" v-if="message.sender">{{
              $t("profile.messages.you")
            }}</span>
          </div>
          <div class="message-content">
            <p>
              {{ message.msg_message }}
            </p>
            <span v-if="message.msg_file" class="msg-link msg">
              <a :href="getMsgUrl(message.msg_file)" target="_blank">{{
                $t("main.downloadFile")
              }}</a>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="control-wrap">
      <InputView
        :label="$t('main.typeMessage')"
        v-model="msg"
        type="textarea"
        @keydown.enter="sendMsg"
        textarea-disable-new-line
        rows="0"
        cols="0"
        max-length="1000"
        full-height
      />
      <FileDisplay
        class="file-display "
        v-if="msgFile"
        :closable="!sendingFile"
        :file-name="msgFile.name"
        @remove-file="removeFile"
      ></FileDisplay>
      <div class="spinner" v-if="sendingFile"></div>
      <div class="button-wrap">
        <input type="file" ref="msgFile" @change="setFile" class="hidden" />

        <div
          class="round-button"
          @click="$refs.msgFile.click()"
          :class="{ hidden: sendingFile }"
        >
          <span>
            <Icon icon="fa6-solid:paperclip" class="icon" />
          </span>
        </div>
        <div
          class="round-button"
          @click="sendMsg"
          :class="{ hidden: sendingFile || (!msg && !msgFile) }"
        >
          <span>
            <Icon icon="fa6-solid:paper-plane" class="icon" />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import defaultProfileImage from "@/assets/images/defaultProfileImage.png";
import eventBus from "@/shared/eventBus.js";
import { mapState } from "vuex";
import { api, baseURL } from "@/api";
import { Icon } from "@iconify/vue2";
import TestInput from "@/components/shared/testInput.vue";
import FileDisplay from "../shared/FileDisplay.vue";
export default {
  name: "chat",
  components: {
    Icon,
    InputView: TestInput,
    FileDisplay
  },
  props: {
    chat: {
      required: true
    }
  },
  data() {
    return {
      msg: "",
      oldScroll: 10000,
      firstLoad: true,
      userData: {},
      msgFile: null,
      showNewMsgToast: false,
      sendingFile: false
    };
  },
  computed: {
    ...mapState([
      "connection",
      "messages",
      "requestingMessages",
      "userImage",
      "userDisplayName"
    ]),
    getMsgFile() {
      return this.msgFile;
    }
  },
  watch: {
    chat: {
      handler: function() {
        this.getUserData(this.chat);
        this.firstLoad = true;
        this.getChat();
        this.msg = "";
      }
    }
  },
  destroyed() {
    eventBus.$off("handleMessage");
    this.$store.commit("setChatVisible", false);
  },
  async mounted() {
    this.getUserData(this.chat);
    await this.getChat();
    eventBus.$on("handleMessage", e => {
      this.onMessage(e, this.chat);
    });
    this.$store.commit("setChatVisible", true);
  },
  methods: {
    onMessage(evt, user) {
      try {
        let newMsg = JSON.parse(evt.data);
        if (newMsg.usr_id_from === user || newMsg.usr_id_to === user) {
          const userName = newMsg.sender
            ? this.userDisplayName
            : this.userData.usr_name;
          this.messages.push({
            ...newMsg,
            usr_name: userName
          });
          this.$nextTick(() => {
            let elem = this.$refs.chatWrap;
            const chatChilds = this.$refs.chatWrap.children;
            const newMessageHeight =
              chatChilds[chatChilds.length - 1].offsetHeight + 7;

            if (
              elem.scrollTop + newMessageHeight ===
              elem.scrollHeight - elem.offsetHeight
            ) {
              this.scrollToBottom();
              let params = {
                id: this.chat
              };
              api
                .seenUserMessages(params)
                .then(() => {
                  eventBus.$emit("refreshMessages");
                })
                .catch(e => {
                  console.log(e);
                });
            } else if (
              elem.scrollTop + newMessageHeight <
              elem.scrollHeight - elem.offsetHeight
            ) {
              this.showNewMsgToast = true;
            }
          });
          eventBus.$emit("refreshMessages");
        } else {
          eventBus.$emit("refreshMessages");
        }
      } catch (e) {
        console.log(e, "failed to parse new msg");
      }
    },
    setFile(e) {
      if (e.target.files[0]) {
        if (e.target.files[0].size > 15000000) {
          e.target.value = null;
          this.msgFile = null;
          this.$toast.error(this.$t("main.bigFile"), {
            duration: 8000
          });
        } else {
          this.msgFile = e.target.files[0];
        }
      } else {
        this.msgFile = null;
      }
    },
    removeFile() {
      this.msgFile = null;
      this.$refs.msgFile.value = null;
    },
    getMsgUrl(url) {
      return baseURL + url + "?sid=" + localStorage.getItem("sid");
    },
    sendMsg() {
      if (this.sendingFile) {
        return;
      }
      if (this.msgFile) {
        this.sendingFile = true;
        let params = new FormData();
        params.append("msg_file", this.msgFile);
        params.append("usr_id_to", this.chat);
        api
          .sendFile(params)
          .then(() => {
            this.removeFile();
            this.getChat();
          })
          .catch(() => {
            this.removeFile();
            this.getChat();
          })
          .finally(() => {
            this.sendingFile = false;
          });
      }
      // else {
      if (this.msg.trim().length === 0) {
        return;
      }
      let params = {
        msg_message: this.msg,
        usr_id_to: this.chat
      };
      this.msg = "";
      this.connection.send(JSON.stringify(params));
      // }
    },
    chatScrolled(e) {
      if (this.requestingMessages) {
        return;
      }
      if (e.target.scrollTop < 10) {
        let params = {
          last_message: this.messages[0].msg_id,
          id: this.chat
        };
        this.$store.dispatch("loadMessagesHistory", params);
      }
      if (e.target.scrollTop < e.target.scrollHeight - 20) {
        this.showNewMsgToast = false;
      }
    },
    async getChat() {
      await this.$store.dispatch("getMessages", this.chat);
      this.$refs.chatWrap.scrollTo({ top: this.$refs.chatWrap.scrollHeight });
    },
    scrollToBottom() {
      this.$refs.chatWrap.scrollTop = this.$refs.chatWrap.scrollHeight;
    },
    imageLink(image) {
      return baseURL + image;
    },
    getImage(sender) {
      if (sender === 1) {
        if (this.userImage) {
          return this.imageLink(this.userImage);
        } else {
          return defaultProfileImage;
        }
      } else {
        if (this.userData.usr_image) {
          return this.imageLink(this.userData.usr_image);
        } else {
          return defaultProfileImage;
        }
      }
    },
    getUserData(id) {
      let params = {
        id: id
      };
      api.getUserInfo(params).then(res => {
        this.userData = res.data.data;
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.chat-wrapper {
  width: 100%;
  box-sizing: border-box;
  padding: 0 40px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  // overflow: hidden;
  .chat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    .left {
      display: flex;
      align-items: center;
      gap: 7px;
      font-size: 22px;
      .email-dim {
        color: $blue-400;
      }
      @media screen and (max-width: 800px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        font-size: 18px;
        max-width: 60vw;
        overflow: auto;
        .email-dim {
          font-size: 14px;
        }
      }
    }
  }
  .icon-wrap {
    display: flex;
    align-items: center;
    gap: 2px;
    color: $gray-blue-1300;
    font-size: 18px;
    cursor: pointer;
    .icon {
      font-size: 24px;
      transform: translateY(2px);
    }
  }
  .message-wrap {
    overflow-y: scroll;
    flex-grow: auto;
    max-height: max(45vh, 400px);
    margin: 10px 0;
    position: relative;
    .new-message-overlay {
      position: sticky;
      top: 0;
      left: 0;
      height: max(45vh, 400px);
      width: calc(100% - 1px);
      float: left;
      margin-right: -100%;
      pointer-events: none;

      .new-message-toast {
        position: absolute;
        bottom: 30px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1;
        border-radius: 20px;
        background-color: $light-blue-600;
        border: 1px solid $light-blue-1000;
        padding: 5px 15px;
        color: $light-blue-1000;
        opacity: 0.4;
        transition: opacity 0.2s;
        display: flex;
        gap: 4px;
        align-items: center;
        cursor: pointer;
        pointer-events: all;
        .icon {
          transform: translateY(3px);
        }
        &:hover {
          opacity: 1;
        }
      }
    }
    .message {
      margin: 7px 0;
      background-color: $gray-blue-400;
      border: 1px solid $gray-blue-700;
      padding: 10px 15px;
      border-radius: 12px;
      text-align: left;
      .message-header {
        display: flex;
        align-items: center;
        gap: 10px;
        color: $blue-400;
        .image-wrap {
          overflow: hidden;
          border-radius: 10px;
          width: 40px;
          height: 40px;
          img {
            width: 100%;
          }
        }
        .you-symbol {
          color: $light-blue-600;
          font-size: 13px;
        }
      }
      .message-content {
        margin-left: 50px;
        padding-top: 8px;
        p {
          margin: 0;
          word-break: break-word;
        }
        span {
          display: block;
          margin-top: 5px;
        }
      }
    }
  }
  .control-wrap {
    display: flex;
    gap: 10px;
    min-height: 100px;
    align-items: center;
    justify-content: flex-end;
    position: relative;
    .file-display {
      max-width: 100%;
      position: absolute;
      bottom: -15px;
    }
    .button-wrap {
      display: flex;
      gap: 10px;
    }
    .round-button {
      padding: 10px;
      border-radius: 12px;
      border: 1px solid $gray-blue-700;
      background: $gray-blue-500;
      color: $gray-blue-1300;
      cursor: pointer;
      transition: color 0.2s, border-color 0.2s;
      font-size: 25px;
      height: fit-content;
      .icon {
        transform: translateY(2px);
      }
      &:hover {
        color: $light-blue-700;
        border-color: $light-blue-700;
      }
    }
    .hidden {
      display: none;
    }
    @media screen and (max-width: 800px) {
      flex-direction: column;

      .file-display {
        position: relative;
        bottom: initial;
      }
    }
  }
  @media screen and (max-width: 1000px) {
    padding: 10px 5px;
    padding: 0 5px 20px;
  }
}
</style>
